import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card, CardBody, Col, Form, Input, Label, Row, FormFeedback, Spinner, Container
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface FormValues {
    name: string;
    gender: string;
    dob: string;
    phone_number: string;
    email: string;
    address: string;
    education: string;
    experience_years: string;
    aadhaar_number: string;
    profile_pic?: FileList | string;
    kyc_document: FileList | string;
    police_verification: FileList | string;
    status: string;
    is_verified: boolean;
    is_enable: boolean;
}

export default function EditBystander() {
    const navigate = useNavigate();
    const location = useLocation();
    const bystanderId = location.state?.id;
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("bystander-management.edit");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [existingFiles, setExistingFiles] = useState({
        profile_pic: '',
        kyc_document: '',
        police_verification: ''
    });

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    useEffect(() => {
        if (!bystanderId) {
            navigate(`${process.env.PUBLIC_URL}/bystander`);
            return;
        }

        const fetchBystander = async () => {
            try {
                const response: any = await apiPost('bystander/viewbyid', { id: bystanderId });
                if (response) {
                    setValue('name', response.name);
                    setValue('gender', response.gender);
                    setValue('dob', response.dob);
                    setValue('phone_number', response.phone_number);
                    setValue('email', response.email || '');
                    setValue('address', response.address);
                    setValue('education', response.education || '');
                    setValue('experience_years', response.experience_years || '');
                    setValue('aadhaar_number', response.aadhaar_number);
                    setValue('status', response.status);
                    setValue('is_verified', response.is_verified);
                    setValue('is_enable', response.is_enable);
                    // For files, we keep the existing strings if new ones aren't uploaded
                    setValue('profile_pic', response.profile_pic);
                    setValue('kyc_document', response.kyc_document);
                    setValue('police_verification', response.police_verification);

                    setExistingFiles({
                        profile_pic: response.profile_pic,
                        kyc_document: response.kyc_document,
                        police_verification: response.police_verification
                    });
                }
            } catch (error) {
                toast.error('Error fetching bystander details');
            } finally {
                setLoading(false);
            }
        };

        fetchBystander();
    }, [bystanderId, setValue, navigate]);

    const onSubmit = async (data: FormValues) => {
        setSaving(true);
        try {
            let profilePicFilename = typeof data.profile_pic === 'string' ? data.profile_pic : '';
            let kycFilename = typeof data.kyc_document === 'string' ? data.kyc_document : '';
            let pvFilename = typeof data.police_verification === 'string' ? data.police_verification : '';

            // If a new file is selected, upload it
            if (data.profile_pic instanceof FileList && data.profile_pic.length > 0) {
                profilePicFilename = await uploadFile(data.profile_pic[0]);
            }
            if (data.kyc_document instanceof FileList && data.kyc_document.length > 0) {
                kycFilename = await uploadFile(data.kyc_document[0]);
            }
            if (data.police_verification instanceof FileList && data.police_verification.length > 0) {
                pvFilename = await uploadFile(data.police_verification[0]);
            }

            const payload: any = {
                ...data,
                profile_pic: profilePicFilename || null,
                kyc_document: kycFilename,
                police_verification: pvFilename,
                is_verified: data.is_verified ? 1 : 0, // Laravel boolean handling
                is_enable: data.is_enable ? 1 : 0 // Laravel boolean handling
            };

            await apiPost(`bystander/update`, { ...payload, id: bystanderId });
            toast.success('Bystander updated successfully');
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/bystander`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error updating bystander');
        } finally {
            setSaving(false);
        }
    };

    if (!canEdit) {
        return (
            <div className="page-body">
                <Breadcrumbs pageTitle="Edit Bystander" parent="General" title="Edit Bystander" />
                <Container fluid>
                    <Card><CardBody><div className="text-center py-5">No Permission</div></CardBody></Card>
                </Container>
            </div>
        );
    }

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "70vh" }}><Spinner color="primary" /></div>
    }

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Bystander" parent="General" title="Edit Bystander" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Label>Full Name <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{ required: "Name is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Enter Full Name" invalid={!!errors.name} autoComplete="off" />
                                                <FormFeedback>{errors.name?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Gender <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="gender"
                                        control={control}
                                        rules={{ required: "Gender is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.gender}>
                                                    <option value="">Select Gender</option>
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </Input>
                                                <FormFeedback>{errors.gender?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Date of Birth <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="dob"
                                        control={control}
                                        rules={{
                                            required: "DOB is required",
                                            validate: (value) => {
                                                const birthDate = new Date(value);
                                                const today = new Date();
                                                let age = today.getFullYear() - birthDate.getFullYear();
                                                const m = today.getMonth() - birthDate.getMonth();
                                                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                                                    age--;
                                                }
                                                return age >= 18 || "Bystander must be at least 18 years old";
                                            }
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    wrapperClassName="w-100"
                                                    className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                    placeholderText="Select Date of Birth"
                                                    dateFormat="yyyy-MM-dd"
                                                    maxDate={new Date()}
                                                    peekNextMonth
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    autoComplete="off"
                                                />
                                                <FormFeedback style={{ display: errors.dob ? "block" : "none" }}>{errors.dob?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Phone Number <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="phone_number"
                                        control={control}
                                        rules={{ required: "Phone number is required" }}
                                        render={({ field }) => (
                                            <>
                                                <PhoneInput
                                                    country={"in"}
                                                    onlyCountries={["in"]}
                                                    disableDropdown
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    inputProps={{
                                                        name: "phone_number",
                                                        placeholder: "Enter Phone Number",
                                                        autoComplete: "none"
                                                    }}
                                                    inputStyle={{
                                                        width: "100%",
                                                        borderColor: errors.phone_number ? "#dc3545" : "#ced4da"
                                                    }}
                                                />
                                                <FormFeedback style={{ display: errors.phone_number ? "block" : "none" }}>{errors.phone_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Email <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address"
                                            }
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} type="email" placeholder="Email Address" invalid={!!errors.email} autoComplete="off" />
                                                <FormFeedback>{errors.email?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Aadhaar Number <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="aadhaar_number"
                                        control={control}
                                        rules={{ required: "Aadhaar number is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Enter Aadhaar Number" invalid={!!errors.aadhaar_number} autoComplete="off" />
                                                <FormFeedback>{errors.aadhaar_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Education <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="education"
                                        control={control}
                                        rules={{ required: "Education is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.education}>
                                                    <option value="">Select Education</option>
                                                    <option value="School Level">School Level</option>
                                                    <option value="Vocational Training">Vocational Training</option>
                                                    <option value="Technical Diploma">Technical Diploma</option>
                                                    <option value="Undergraduate">Undergraduate</option>
                                                    <option value="Postgraduate">Postgraduate</option>
                                                </Input>
                                                <FormFeedback>{errors.education?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Experience (Years) <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="experience_years"
                                        control={control}
                                        rules={{ required: "Experience is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} type="number" placeholder="Years of Experience" invalid={!!errors.experience_years} autoComplete="off" />
                                                <FormFeedback>{errors.experience_years?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Label>Address <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="address"
                                        control={control}
                                        rules={{ required: "Address is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Complete Address" invalid={!!errors.address} rows={3} />
                                                <FormFeedback>{errors.address?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Status <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{ required: "Status is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.status}>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Approved">Approved</option>
                                                    <option value="Reject">Reject</option>
                                                </Input>
                                                <FormFeedback>{errors.status?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Is Verified <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="is_verified"
                                        control={control}
                                        render={({ field }) => (
                                            <Input type="select" value={field.value ? "1" : "0"} onChange={(e) => field.onChange(e.target.value === "1")}>
                                                <option value="1">Yes</option>
                                                <option value="0">No</option>
                                            </Input>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Is Enabled <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="is_enable"
                                        control={control}
                                        render={({ field }) => (
                                            <Input type="select" value={field.value ? "1" : "0"} onChange={(e) => field.onChange(e.target.value === "1")}>
                                                <option value="1">Yes</option>
                                                <option value="0">No</option>
                                            </Input>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>Profile Picture <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="profile_pic"
                                        control={control}
                                        rules={{ required: "Profile picture is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*"
                                                    invalid={!!errors.profile_pic}
                                                />
                                                <FormFeedback>{errors.profile_pic?.message}</FormFeedback>
                                                {existingFiles.profile_pic && (
                                                    <div className="mt-1">
                                                        <a href={`${mediaUrl}${existingFiles.profile_pic}`} target="_blank" rel="noreferrer" className="text-primary small fw-bold">
                                                            View Current Image
                                                        </a>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                    <small className="text-muted d-block mt-1">Re-upload required to update</small>
                                </Col>

                                <Col md={4}>
                                    <Label>KYC Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="kyc_document"
                                        control={control}
                                        rules={{ required: "KYC document is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*,.pdf"
                                                    invalid={!!errors.kyc_document}
                                                />
                                                <FormFeedback>{errors.kyc_document?.message}</FormFeedback>
                                                {existingFiles.kyc_document && (
                                                    <div className="mt-1">
                                                        <a href={`${mediaUrl}${existingFiles.kyc_document}`} target="_blank" rel="noreferrer" className="text-primary small fw-bold">
                                                            View Current Document
                                                        </a>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                    <small className="text-muted d-block mt-1">Re-upload required to update</small>
                                </Col>

                                <Col md={4}>
                                    <Label>Police Verification <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="police_verification"
                                        control={control}
                                        rules={{ required: "Police verification is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*,.pdf"
                                                    invalid={!!errors.police_verification}
                                                />
                                                <FormFeedback>{errors.police_verification?.message}</FormFeedback>
                                                {existingFiles.police_verification && (
                                                    <div className="mt-1">
                                                        <a href={`${mediaUrl}${existingFiles.police_verification}`} target="_blank" rel="noreferrer" className="text-primary small fw-bold">
                                                            View Current Verification
                                                        </a>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                    <small className="text-muted d-block mt-1">Re-upload required to update</small>
                                </Col>
                            </Row>

                            <div className="mt-5">
                                <Btn color="primary" disabled={saving} type="submit" className="px-5">
                                    {saving ? <>Updating <Spinner size="sm" /></> : "Update Bystander"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
